import { CheckBox } from '@iwoca/orion';
import {
    Control,
    Controller,
    FieldErrors,
    UseFormRegister,
    UseFormSetValue,
    UseFormWatch,
    useFieldArray
} from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { AddressQuestion } from './AddressQuestion';
import { ResidentialStatusQuestion } from './ResidentialStatusQuestion';
import { getCountry } from '../../../../iwIntlProvider/CountryProvider';
import { PlusSVG } from '../../../../svgComponents/plusSVG/index';
import { TrashcanSVG } from '../../../../svgComponents/trashcanSVG/index';
import styles from '../PersonModal.module.css';
import {
    TDeserialisedAddresses,
    TPersonFormValues
} from '../PersonModal.types';

type AddressDetailsProps = {
    register: UseFormRegister<TPersonFormValues>;
    control: Control<TPersonFormValues>;
    errors: FieldErrors<TPersonFormValues>;
    setValue: UseFormSetValue<TPersonFormValues>;
    watch: UseFormWatch<TPersonFormValues>;
    isGuarantor: boolean;
    isApplicant: boolean;
    isEditingPerson: boolean;
    hasCreditDecision: boolean;
    showManualAddresses: Record<number, boolean>;
    fetchAddresses: (postcode: string) => void;
    toggleManualAddressState: (addressIndex: number) => void;
    fetchedAddresses: TDeserialisedAddresses;
    companyType?: string;
};

export const AddressDetails = ({
    register,
    control,
    errors,
    setValue,
    watch,
    isGuarantor,
    isApplicant,
    isEditingPerson,
    hasCreditDecision,
    showManualAddresses,
    fetchAddresses,
    toggleManualAddressState,
    fetchedAddresses,
    companyType
}: AddressDetailsProps) => {
    const intl = useIntl();
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'addresses'
    });

    const disableEditingAdditionalGuarantor =
        hasCreditDecision && isEditingPerson && isGuarantor && !isApplicant;

    return (
        <>
            <div className={styles.subTitle}>
                <FormattedMessage
                    id="_.person.address_details"
                    defaultMessage="Address details"
                />
            </div>
            {fields.map((field, index) => {
                const isPrimaryAddress = watch(
                    `addresses.${index}.isPrimaryAddress` as `addresses.${number}.isPrimaryAddress`
                );
                const addressStyles =
                    index === 0
                        ? `${styles.addressWrapper} mb-xl`
                        : `${styles.removeWrapper} ${styles.previousAddressWrapper} mb-xl`;

                const showResidentialStatus =
                    isPrimaryAddress &&
                    ((getCountry() === 'UK' && companyType === 'sole_trader') ||
                        getCountry() === 'DE');

                return (
                    <div
                        className={addressStyles}
                        key={field.id}
                        data-testid="address-question"
                    >
                        <AddressQuestion
                            addressIndex={index}
                            register={register}
                            setValue={setValue}
                            watch={watch}
                            onManualAddressToggle={toggleManualAddressState}
                            fetchedAddresses={fetchedAddresses}
                            fetchAddresses={fetchAddresses}
                            showManualAddresses={showManualAddresses}
                            hasCreditDecision={hasCreditDecision}
                            isEditingPerson={isEditingPerson}
                            isGuarantor={isGuarantor}
                            isApplicant={isApplicant}
                            errors={errors}
                        />
                        {showResidentialStatus && (
                            <ResidentialStatusQuestion
                                addressIndex={index}
                                register={register}
                            />
                        )}
                        {!isPrimaryAddress && (
                            <div
                                className={styles.removeBtn}
                                onClick={() => remove(index)}
                                data-testid="remove-address-button"
                            >
                                <TrashcanSVG />
                            </div>
                        )}
                    </div>
                );
            })}
            {getCountry() === 'UK' && !disableEditingAdditionalGuarantor && (
                <button
                    className={styles.addBtn}
                    type="button"
                    data-testid="add-another-address-button"
                    onClick={() => append({ isPrimaryAddress: false })}
                >
                    <PlusSVG className={styles.addIcon} />
                    <FormattedMessage
                        id="_.person.add_another_address"
                        defaultMessage="Add previous address"
                    />
                </button>
            )}
            <div className={styles.marketingCheckbox}>
                <Controller
                    control={control}
                    name="marketing"
                    render={({ field: { onChange, value, name, ref } }) => (
                        <CheckBox
                            ref={ref}
                            name={name}
                            data-testid="marketing"
                            label={intl.formatMessage({
                                id: '_.person.marketing',
                                defaultMessage: 'News and updates from iwoca'
                            })}
                            checked={!!value}
                            onChange={(e) => onChange(e.target.checked)}
                        />
                    )}
                />
            </div>
        </>
    );
};
